/* eslint-disable indent */
import { BreadcrumbList } from '@/components/Breadcrumbs/BreadcrumbList';
import { BreadcrumbItem } from '@/components/Breadcrumbs/BreadcrumbItem';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { PageHeader } from '@/components/Header/Page/PageHeader';
import { useNavigate, useParams } from 'react-router-dom';
import { TabItem } from '@/components/Tab/TabItem';
import { TabList } from '@/components/Tab/TabList';
import Loader from '@/components/Loading/Loader';
import { NotFound } from '@/components/Message/NotFound';
import { VehiculeInfosManagement } from '../components/VehiculeInfosManagement';
import {
  TVehiculeSchema,
  vehiculeSchema,
} from '@/schemas/vehicule/vehicule.schema';
import {
  useCreateVehicule,
  useGetVehiculeById,
  useUpdateVehicule,
} from '@/hooks/vehicule.hooks';
// import { VehiculeCheckStatsManagement } from '../components/VehiculeCheckStatsManagement';
import { useTranslation } from 'react-i18next';
import { VehiculeOwnerType } from '@/api/entities/vehicule';
import { useGetTab } from '@/hooks/tab';
import { EditVehiculeTab } from '@/type/ui/tab/vehiculeTab/edit-tab.type';
import { useGetAllVehiculeChecks } from '@/hooks/vehicule-check.hooks';
import { TVehiculeCheckSchema } from '@/schemas/vehicule-check/vehicule-check.schema';
import { VehiculeCheckTableHeaders } from '@/modules/VehiculeCheck/components/VehiculeCheckTableHeaders';
import { VehiculeCheckTableData } from '@/modules/VehiculeCheck/components/VehiculeCheckTableData';
import { VehiculeCheckTableView } from '@/modules/VehiculeCheck/components/VehiculeCheckTableView';
import { AllSelectedTableItemsProvider } from '@/providers/AllSelectedTableItemsProvider';
import { TitleSection } from '@/components/Header/Section/TitleSection';
// import { CarVehiculeCheckList } from '../components/CarVehiculeCheckList';

export const EditVehiculePage = () => {
  const { id } = useParams();
  const { data, isLoading } = useGetVehiculeById(`${id}`);
  const { t } = useTranslation('vehicule');
  const { t: tInspection } = useTranslation('inspection');
  const navigate = useNavigate();

  const { currentTab, navigateToTab } = useGetTab({
    defaultTab: EditVehiculeTab.INFOS,
  });

  const onLicensePlateText = isLoading
    ? ''
    : `${t('common:on_upon').toLowerCase()} ${t(
        'theVehiculeWithLicensePlate',
      ).toLowerCase()} ${data?.data?.licensePlateNumber}`;

  const {
    watch: watchVehicule,
    control: vehiculeControl,
    register: registerVehicule,
    setValue: setVehiculeValue,
    setError: setVehiculeError,
    handleSubmit: handleSubmitVehicule,
    formState: { errors: vehiculeErrors, isSubmitting: isSubmittingVehicule },
  } = useForm<TVehiculeSchema>({
    resolver: zodResolver(vehiculeSchema),
  });

  const { mutate: mutateUpdateVehicule, isPending: isPendingUpdateVehicule } =
    useUpdateVehicule({
      t,
      handleSuccess: () => navigate('/vehicules'),
    });

  const { mutate: mutateCreateVehicule, isPending: isPendingCreateVehicule } =
    useCreateVehicule({
      t,
      handleSuccess: () => navigate('/vehicules'),
    });

  const onSubmitVehiculeInfos = (vehicule: TVehiculeSchema) => {
    let vehiculeWithId = { id, ...vehicule };

    const { ownerType, ownerGroupId, ownerCompanyId } = vehiculeWithId;

    if (
      (ownerType == VehiculeOwnerType.GROUP && !ownerGroupId) ||
      (ownerType == VehiculeOwnerType.COMPANY && !ownerCompanyId)
    ) {
      vehiculeWithId = {
        ...vehiculeWithId,
        ownerType: null,
        ownerGroupId: ownerGroupId || null,
        ownerCompanyId: ownerCompanyId || null,
      };
    }

    if (vehicule?.isToAdd) {
      delete vehicule?.isToAdd;
      mutateCreateVehicule(vehicule);
    } else {
      delete vehiculeWithId?.isToAdd;
      mutateUpdateVehicule(vehiculeWithId);
    }
  };

  return (
    <>
      <PageHeader
        backToLink="/vehicules"
        title={t('theVehicule')}
        subTitle={
          data?.data?.licensePlateNumber ? (
            <>
              <span className="font-medium">{`${t(
                'common:registered',
              )} :`}</span>
              &nbsp;
              {data?.data?.licensePlateNumber}
            </>
          ) : data?.data?.model && data?.data?.brand ? (
            <div>{`${data?.data?.model} ${data?.data?.brand}`}</div>
          ) : undefined
        }
        subTitleIconClassName="ki-parcel"
        headingElement={
          <BreadcrumbList>
            <BreadcrumbItem text={t('vehiculeList')} linkTo="/vehicules" />
            <BreadcrumbItem text={data?.data?.licensePlateNumber} active />
          </BreadcrumbList>
        }
      />
      {!isLoading && !data?.data ? (
        <NotFound text={`${t('thisVehiculeDoesNotExist')}...`} />
      ) : (
        <div>
          <TabList className="mb-5">
            <TabItem
              text={t('common:informations')}
              leftIcon={<i className="ki-outline ki-badge" />}
              active={currentTab === EditVehiculeTab.INFOS}
              onChange={() => navigateToTab(EditVehiculeTab.INFOS)}
            />
            <TabItem
              text={t('common:inspections')}
              leftIcon={<i className="ki-outline ki-tablet-ok" />}
              active={currentTab === EditVehiculeTab.VEHICULE_CHECKS}
              onChange={() => navigateToTab(EditVehiculeTab.VEHICULE_CHECKS)}
            />
          </TabList>
          {currentTab === EditVehiculeTab.INFOS && (
            <VehiculeInfosManagement<TVehiculeSchema>
              t={t}
              data={!isLoading && data?.data}
              watch={watchVehicule}
              control={vehiculeControl}
              register={registerVehicule}
              setValue={setVehiculeValue}
              setError={setVehiculeError}
              errors={vehiculeErrors}
              submitDisabled={
                isSubmittingVehicule ||
                isPendingUpdateVehicule ||
                isPendingCreateVehicule
              }
              onSubmit={handleSubmitVehicule(onSubmitVehiculeInfos)}
            />
          )}
          {currentTab === EditVehiculeTab.VEHICULE_CHECKS && (
            // <VehiculeCheckStatsManagement
            //   t={t}
            //   linkTo={`/vehicule-checks/vehicule/${id}`}
            //   numberOfVehiculeChecks={totalActiveVehiculeChecks?.data || 0}
            // />
            <AllSelectedTableItemsProvider>
              <VehiculeCheckTableView<TVehiculeCheckSchema>
                t={tInspection}
                cardTitle={
                  <TitleSection
                    text={
                      data?.data?.licensePlateNumber
                        ? `${t('common:inspections')} ${onLicensePlateText}`
                        : t('inspectionsOnThisVehicule')
                    }
                    defaultIconClassName="ki-parcel"
                  />
                }
                receivedVehiculeId={id}
                useGetAllData={useGetAllVehiculeChecks}
                tableHeaders={VehiculeCheckTableHeaders}
                tableData={VehiculeCheckTableData}
              />
            </AllSelectedTableItemsProvider>
          )}
        </div>
      )}
      {(isLoading ||
        isSubmittingVehicule ||
        isPendingUpdateVehicule ||
        isPendingCreateVehicule) && <Loader />}
    </>
  );
};
